/* eslint-disable */
/**
 * OekonomiPanel.jsx — Panel i Sager-stil til OS-dashboard
 *
 * Datapunkter og status-mærkning:
 *   /api/os/intern/okonomi        → LIVE (Dinero) eller FEJL (API-fejl)
 *     Felter: dinero_forbundet (bool), mrr, arr, banksaldo (objekt),
 *             maaned_total, aar_total, udestaaende_total, udestaaende_forfalden,
 *             udestaaende_antal, udestaaende_forfalden_antal, kladder_antal,
 *             kladder_total, loenforpligtelse_md, pr_maaned (objekt),
 *             scenarier (objekt), fejl
 *   /api/os/loenforpligtelser     → LIVE (os.loenforpligtelser-tabel)
 *     Felter: forpligtelser[{id,navn,rolle,beloeb_md,aktiv}], total_md
 *
 * Tre tilstande for Dinero:
 *   (a) dinero_forbundet=false + fejl=".."  → API-fejl / ikke forbundet
 *   (b) dinero_forbundet=true + maaned_total=0 → Forbundet, nyt selskab uden bogførte fakturaer
 *   (c) dinero_forbundet=true + maaned_total>0 → Forbundet med data
 *
 * KPI-regler (v1.6.49):
 *   - MRR: vises som "0 kr" for nyt selskab (aldrig "—"/null/tom)
 *   - ARR: vises KUN som tal når MRR > 0 — blank (—) ved MRR=0
 *   - Omsætning år-til-dato: "0 kr" for nyt selskab
 *   - Banksaldo: live KPI-kort — beregnet fra Dinero /entries + /accounts/deposit.
 *     BEMÆRK: Dinero har ingen dedikeret banksaldo-endpoint (verificeret 2026-06-14).
 *     Saldo beregnes ved at summere bogførte posteringer på depositkonti i Dinero.
 *   - Kladder: aggregeret antal + beløb — ALDRIG individuelle kundenavne (GDPR)
 *   - Kladder tæller ALDRIG med i omsætning/MRR
 *   - Scenarier fra os.tilstand (ikke hardkodet)
 *   - Lønforpligtelse-tabel — synkroniseres automatisk fra Salary.dk
 *   - Løn dækket: grøn JA / rød NEJ / grå Ikke forbundet
 */

import React, { useState, useCallback } from 'react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  GrafLinje,
  SektionsHeader,
  AfventerBadge,
  SammenklappeligSektion,
  LoenforpligtelserKortListe,
  useApiPolling,
  useWindowWidth,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
  FONT,
} from '../komponenter/index';
import { Boks } from '../ui/Boks';

// ─── Hjælpefunktioner ─────────────────────────────────────────────────────────

function kroner(tal) {
  if (tal == null) return '—';
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(tal);
}

function kortKroner(tal) {
  if (tal == null) return '—';
  if (tal >= 1000000) return `${(tal / 1000000).toFixed(1)} mio.`;
  if (tal >= 1000)    return `${Math.round(tal / 1000)}k`;
  return `${tal}`;
}

function maaCode(kode) {
  const map = {
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
    '05': 'Maj', '06': 'Jun', '07': 'Jul', '08': 'Aug',
    '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec',
  };
  if (!kode) return '';
  const parts = kode.split('-');
  return parts.length === 2 ? `${map[parts[1]] || parts[1]}` : kode;
}


// ─── Scenarie-bjælke ──────────────────────────────────────────────────────────

function ScenarieBjaelke({ label, maal, nuværende, farve }) {
  const pct = maal > 0 ? Math.min(100, Math.round((nuværende / maal) * 100)) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: TEKST.primær, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, color: TEKST.sekundær }}>
          {kroner(nuværende)} / <span style={{ color: farve }}>{kroner(maal)}</span>
        </span>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 7, overflow: 'hidden' }}>
        <div style={{
          width:      `${pct}%`,
          height:     '100%',
          background: farve,
          borderRadius: 4,
          transition: 'width .6s ease',
          minWidth:   pct > 0 ? 4 : 0,
        }} />
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: TEKST.sekundær, textAlign: 'right' }}>{pct}% af mål</div>
    </div>
  );
}


// ─── PANEL ────────────────────────────────────────────────────────────────────

export default function OekonomiPanel() {
  // ── Responsive ─────────────────────────────────────────────────────────────
  const isMobil = useWindowWidth() < 600;

  // ── Data-fetching ──────────────────────────────────────────────────────────
  const {
    data:      okonomi,
    loading:   okonomiLoading,
    fejl:      oekoFejl,
    opdateret: okonomiOpdateret,
    refresh:   okonomiRefresh,
  } = useApiPolling('/os/intern/okonomi', 60000);

  const {
    data:      loenData,
    loading:   loenLoading,
    refresh:   loenRefresh,
  } = useApiPolling('/os/loenforpligtelser', 60000);

  // Første-load flags: skeleton/henter-tekst vises KUN ved første hentning.
  // Ved baggrunds-refresh forbliver eksisterende data synlige → scroll bevares.
  const okonomiFirstLoad = okonomiLoading && !okonomi;
  const loenFirstLoad    = loenLoading && !loenData;

  // ── State ──────────────────────────────────────────────────────────────────
  const [refreshing, setRefreshing]   = useState(false);

  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([okonomiRefresh(), loenRefresh()]);
    setRefreshing(false);
  }, [okonomiRefresh, loenRefresh]);

  // ── Udled data ─────────────────────────────────────────────────────────────

  // harDinero: backend eksponerer nu dinero_forbundet eksplicit.
  // Fallback: tjek om maaned_total er til stede (bagudkompatibilitet).
  const harDinero       = !!(okonomi && (okonomi.dinero_forbundet === true || (!okonomi.fejl && okonomi.maaned_total != null)));

  const loenForpligt    = loenData?.total_md ?? okonomi?.loenforpligtelse_md ?? null;
  const maanedTotal     = okonomi?.maaned_total ?? null;
  const kladderTotal    = okonomi?.kladder_total ?? 0;
  // loenDaekket: null=ukendt, true/false=JA/NEJ (0 omsætning → NEJ, korrekt)
  const loenDaekket     = (harDinero && maanedTotal != null && loenForpligt != null)
    ? (maanedTotal + (kladderTotal ?? 0)) >= loenForpligt
    : null;
  // aarTotal: 0 er valid Dinero-respons for nyt selskab — vis det som tal, ikke "—"
  const aarTotal        = (harDinero && okonomi.aar_total != null) ? okonomi.aar_total : null;
  const udestaaende     = okonomi?.udestaaende_total ?? null;
  const udestaaendeAntal = okonomi?.udestaaende_antal ?? null;

  // MRR: 0 for nyt selskab (forbundet, ingen historik) — aldrig "—" eller null
  // Backend returnerer mrr=0 (ikke null) når Dinero er forbundet og der er ingen historik
  const mrr = okonomi?.mrr ?? null;
  // ARR: kun vis når MRR > 0 — ingen projektion på 0
  const arr = (okonomi?.arr != null && okonomi.arr > 0) ? okonomi.arr : null;

  // Banksaldo — beregnet fra Dinero /entries + /accounts/deposit (ingen dedikeret endpoint)
  const banksaldo       = okonomi?.banksaldo ?? null;
  const banksaldoVaerdi = banksaldo?.saldo ?? null;
  const banksaldoNavn   = banksaldo?.konto_navn ?? null;
  const banksaldoKilde  = banksaldo?.kilde ?? null;

  // Kladder — kun informativt, tæller aldrig med i omsætning.
  // GDPR: vis KUN antal og samlet beløb — ALDRIG individuelle kundenavne.
  const kladderAntal    = okonomi?.kladder_antal ?? 0;
  const loenDaekningPct = (harDinero && loenForpligt > 0)
    ? Math.min(100, Math.round(((maanedTotal + (kladderTotal ?? 0)) / loenForpligt) * 100))
    : null;

  // Månedsgraf fra pr_maaned-objektet
  const maanedGrafData = (() => {
    const pm = okonomi?.pr_maaned;
    if (!pm || typeof pm !== 'object') return null;
    const entries = Object.entries(pm)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([kode, val]) => ({
        label: maaCode(kode),
        værdi: typeof val === 'object' ? (val.total_inkl_moms || 0) : (parseFloat(val) || 0),
      }));
    return entries.length >= 2 ? entries : null;
  })();

  // Scenarier: prioritér fra okonomi.scenarier, ellers byg fra loenforpligtelse-kontekst
  const scenarieListe = (() => {
    // Endpoint kan returnere scenarier som objekt eller via cockpit
    const sc = okonomi?.scenarier;
    if (!sc) return null;
    const liste = [];
    if (sc.konservativ?.aarlig) liste.push({ label: 'Konservativt', maal: sc.konservativ.aarlig, farve: '#475569' });
    else if (typeof sc.konservativ === 'number') liste.push({ label: 'Konservativt', maal: sc.konservativ, farve: '#475569' });
    if (sc.realistisk?.aarlig) liste.push({ label: 'Realistisk', maal: sc.realistisk.aarlig, farve: '#3b82f6' });
    else if (typeof sc.realistisk === 'number') liste.push({ label: 'Realistisk', maal: sc.realistisk, farve: '#3b82f6' });
    if (sc.ambitioes?.aarlig) liste.push({ label: 'Ambitiøst', maal: sc.ambitioes.aarlig, farve: '#22c55e' });
    else if (typeof sc.ambitioes === 'number') liste.push({ label: 'Ambitiøst', maal: sc.ambitioes, farve: '#22c55e' });
    return liste.length > 0 ? liste : null;
  })();

  // Lønforpligtelser-tabel
  const loenForpligtelser = loenData?.forpligtelser || [];
  const loenKolonner = [
    {
      key: 'navn', label: 'Navn',
      render: v => <span style={{ color: TEKST.primær, fontWeight: 600 }}>{v}</span>,
    },
    {
      key: 'rolle', label: 'Rolle',
      render: v => <span style={{ color: TEKST.sekundær }}>{v || '—'}</span>,
    },
    {
      key: 'beloeb_md', label: 'Beløb/md.',
      align: 'right',
      render: v => <span style={{ fontWeight: 700, color: STATUS.konfigureret }}>{kroner(parseInt(v))}</span>,
    },
    {
      key: 'aktiv', label: 'Aktiv',
      align: 'center',
      render: v => (
        <StatusBadge
          status={v ? 'LIVE' : 'IKKE FORBUNDET'}
          tekst={v ? 'Aktiv' : 'Inaktiv'}
          lille
        />
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: FONT.family }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Økonomi"
        opdateret={okonomiOpdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        højreIndhold={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Dinero-forbindelsesstatus: tre tilstande */}
            {harDinero && aarTotal === 0 && (
              <StatusBadge status="KONFIGURERET" tekst="Dinero forbundet — nyt selskab" />
            )}
            {harDinero && aarTotal > 0 && (
              <StatusBadge status="LIVE" tekst="Dinero live" />
            )}
            {!harDinero && (
              <StatusBadge status="FEJL" tekst="Dinero ikke forbundet" />
            )}
            {loenDaekket === true  && <StatusBadge status="LIVE" tekst="Løn dækket" />}
            {loenDaekket === false && <StatusBadge status="FEJL" tekst="Løn ikke dækket" puls />}
            {loenDaekket === null  && harDinero && <StatusBadge status="KONFIGURERET" tekst="Løn: ingen data endnu" />}
          </div>
        }
        style={{ marginBottom: 20 }}
      />

      {/* ── KPI-række ───────────────────────────────────────────────────────── */}
      {/* Mobil: fast 2-kolonne grid (2x3 = 6 kort).
          Desktop: flex-wrap (uændret — pixel-identisk med v1.6.59). */}
      <div style={isMobil ? {
        display:               'grid',
        gridTemplateColumns:   '1fr 1fr',
        gap:                   12,
        marginBottom:          18,
      } : {
        display:    'flex',
        gap:        14,
        flexWrap:   'wrap',
        marginBottom: 22,
      }}>
        {/* MRR: vis "0 kr" for nyt selskab — aldrig "—" eller blank */}
        <KpiKort
          label="MRR"
          value={harDinero ? (mrr != null ? kortKroner(mrr) : '0') : '—'}
          sub={harDinero && mrr === 0 ? 'Nyt selskab — ingen historik endnu' : undefined}
          farve={harDinero ? (mrr > 0 ? STATUS.live : STATUS.konfigureret) : STATUS.ikkeForbundet}
          ikon="↻"
          badge={harDinero ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={okonomiFirstLoad}
          mobil={isMobil}
        />
        {/* ARR: kun vis som tal når MRR > 0 — blank (—) ved MRR=0 */}
        <KpiKort
          label="ARR"
          value={harDinero ? (arr != null ? kortKroner(arr) : '—') : '—'}
          sub={harDinero && arr == null ? 'Ingen ARR-projektion ved 0 MRR' : undefined}
          farve={harDinero ? (arr != null ? STATUS.live : STATUS.konfigureret) : STATUS.ikkeForbundet}
          ikon="↗"
          badge={harDinero ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={okonomiFirstLoad}
          mobil={isMobil}
        />
        {/* Omsætning regnskabsår (1/6-31/5): 0 kr er korrekt for nyt selskab */}
        <KpiKort
          label={okonomi?.regnskabsaar ? `Omsætning ${okonomi.regnskabsaar}` : 'Omsætning regnskabsår'}
          kortLabel="OMSÆTNING"
          value={aarTotal != null ? kroner(aarTotal) : '—'}
          sub={harDinero && aarTotal === 0 ? 'Nyt selskab — ingen bogførte fakturaer' : undefined}
          farve={aarTotal != null ? (aarTotal > 0 ? STATUS.live : STATUS.konfigureret) : STATUS.ikkeForbundet}
          ikon="▦"
          badge={harDinero ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={okonomiFirstLoad}
          mobil={isMobil}
        />
        {/* Banksaldo: beregnet fra Dinero /entries + /accounts/deposit */}
        <KpiKort
          label="Banksaldo"
          value={
            !harDinero
              ? '—'
              : banksaldoKilde === 'fejl'
                ? '—'
                : banksaldoVaerdi != null
                  ? kroner(banksaldoVaerdi)
                  : '—'
          }
          sub={
            harDinero && banksaldoNavn
              ? banksaldoNavn
              : harDinero && banksaldoKilde === 'ingen_data'
                ? 'Ingen bogf. posteringer endnu'
                : harDinero && banksaldoKilde === 'fejl'
                  ? 'Kunne ikke hentes'
                  : undefined
          }
          farve={
            harDinero && banksaldoVaerdi != null
              ? (banksaldoVaerdi > 0 ? STATUS.live : STATUS.opmærksomhed)
              : STATUS.ikkeForbundet
          }
          ikon="⬤"
          badge={harDinero ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={okonomiFirstLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Udestående"
          value={harDinero ? kroner(udestaaende ?? 0) : '—'}
          farve={harDinero ? ((udestaaende || 0) > 0 ? STATUS.opmærksomhed : STATUS.live) : STATUS.ikkeForbundet}
          ikon="◎"
          badge={harDinero ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={okonomiFirstLoad}
          mobil={isMobil}
        />
        <KpiKort
          label="Lønforpligtelse/md."
          kortLabel="LØNNFORPLIGT."
          value={kroner(loenForpligt)}
          farve={loenDaekket === false ? STATUS.fejl : STATUS.konfigureret}
          ikon="⬡"
          badge="KONFIGURERET"
          loading={loenFirstLoad}
          mobil={isMobil}
        />
      </div>

      {loenDaekningPct != null && loenDaekket != null && (
        <div style={{ marginTop: 12, marginBottom: 8 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            color: TEKST.sekundær,
            marginBottom: 4
          }}>
            <span>Løndækning denne måned</span>
            <span>{loenDaekningPct}%</span>
          </div>
          <div style={{
            height: 6,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: Math.min(100, loenDaekningPct) + '%',
              borderRadius: 3,
              background: loenDaekket ? '#0ca30c' : '#e34948',
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>
      )}

      {/* ── Kladder-linje (bonus) ────────────────────────────────────────────── */}
      {harDinero && kladderAntal > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'rgba(245,158,11,0.07)',
          border: '1px solid rgba(245,158,11,0.22)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 22,
        }}>
          <span style={{ fontSize: 15 }}>◑</span>
          <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 600 }}>
            {kladderAntal} kladde{kladderAntal !== 1 ? 'r' : ''} klar til afsendelse
          </span>
          <span style={{ fontSize: 13, color: TEKST.sekundær }}>
            — {kroner(kladderTotal)} (tæller ikke med i omsætning)
          </span>
          <StatusBadge status="KONFIGURERET" tekst="KLADDE" lille style={{ marginLeft: 'auto' }} />
        </div>
      )}

      {/* ── To-kolonne: Månedsgraf + Scenarier — sekundært indhold, sammenklap på mobil ── */}
      <SammenklappeligSektion
        titel="Omsætning & mål"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22 }}
      >
      <div style={{ display: 'grid', gridTemplateColumns: isMobil ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginBottom: isMobil ? 0 : 0 }}>

        {/* Månedsomsætning-graf */}
        <Boks>
          <SektionsHeader label="Månedsomsætning" style={{ marginBottom: 12 }} />
          {okonomiFirstLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : maanedGrafData ? (
            <>
              {/* OS-15: YAxis og XAxis giver omsætningens skala og måneder */}
              <GrafLinje
                data={maanedGrafData}
                farve={STATUS.live}
                højde={120}
                fyldt={true}
                viserYAkse={true}
                viserXAkse={true}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                Omsætning inkl. moms per måned
                <StatusBadge status={harDinero ? 'LIVE' : 'KONFIGURERET'} lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          ) : (
            <TomTilstand
              titel={harDinero ? 'Ingen bogført omsætning endnu' : 'Dinero ikke forbundet'}
              besked={
                okonomi?.fejl ||
                (harDinero
                  ? 'Grafen vises automatisk når de første fakturaer er bogført. Kladder tæller ikke med.'
                  : 'Dinero-API svarede ikke. Tjek forbindelsen.')
              }
              ikon="↗"
              badge={harDinero ? 'KONFIGURERET' : 'FEJL'}
            />
          )}
        </Boks>

        {/* Scenarie-bjælker */}
        <Boks>
          <SektionsHeader label="Omsætningsmål 2026" style={{ marginBottom: 14 }} />
          {okonomiFirstLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : scenarieListe ? (
            <>
              {scenarieListe.map(sc => (
                <ScenarieBjaelke
                  key={sc.label}
                  label={sc.label}
                  maal={sc.maal}
                  nuværende={aarTotal || 0}
                  farve={sc.farve}
                />
              ))}
              <div style={{ marginTop: 4, fontSize: 11, color: TEKST.sekundær }}>
                Scenarier fra os.tilstand
                <StatusBadge status="KONFIGURERET" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          ) : (
            <TomTilstand
              titel="Ingen scenarier sat"
              besked="Sæt scenarie_konservativ, scenarie_realistisk og scenarie_ambitioes i os.tilstand."
              ikon="▦"
              badge="IKKE FORBUNDET"
            />
          )}
        </Boks>
      </div>
      </SammenklappeligSektion>

      {/* ── Udestående fakturaer — sekundært, sammenklap på mobil ─────────────── */}
      <SammenklappeligSektion
        titel="Udestående fakturaer"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22 }}
      >
      <Boks style={{ marginBottom: isMobil ? 0 : 0 }}>
        <SektionsHeader
          label="Udestående fakturaer"
          højreIndhold={
            udestaaende != null && udestaaende > 0
              ? <AfventerBadge antal={udestaaendeAntal || 0} label={`faktura${(udestaaendeAntal || 0) !== 1 ? 'er' : ''}`} />
              : null
          }
          style={{ marginBottom: 14 }}
        />
        {okonomiFirstLoad ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
        ) : !harDinero ? (
          <TomTilstand
            titel={harDinero ? 'Ingen udestående fakturaer' : 'Dinero ikke forbundet'}
            besked={
              okonomi?.fejl ||
              (harDinero
                ? 'Der er ingen godkendte eller forventede fakturaer i øjeblikket.'
                : 'Dinero-API svarede ikke. Tjek forbindelsen.')
            }
            ikon="◎"
            badge={harDinero ? 'LIVE' : 'FEJL'}
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {/* Totalt udestående */}
            <div style={{
              background: (udestaaende || 0) > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.06)',
              border: `1px solid ${(udestaaende || 0) > 0 ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.20)'}`,
              borderRadius: RADIUS.kort - 2, padding: '14px 16px',
            }}>
              <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 4 }}>Totalt udestående</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: (udestaaende || 0) > 0 ? STATUS.opmærksomhed : STATUS.live }}>
                {kroner(udestaaende)}
              </div>
              {udestaaendeAntal != null && (
                <div style={{ fontSize: 12, color: TEKST.sekundær, marginTop: 4 }}>
                  {udestaaendeAntal} faktura{udestaaendeAntal !== 1 ? 'er' : ''}
                </div>
              )}
            </div>
            {/* Forfalden */}
            {okonomi.udestaaende_forfalden != null && (
              <div style={{
                background: okonomi.udestaaende_forfalden > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.06)',
                border: `1px solid ${okonomi.udestaaende_forfalden > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.20)'}`,
                borderRadius: RADIUS.kort - 2, padding: '14px 16px',
              }}>
                <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 4 }}>Forfalden (ubetalt)</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: okonomi.udestaaende_forfalden > 0 ? STATUS.fejl : STATUS.live }}>
                  {kroner(okonomi.udestaaende_forfalden)}
                </div>
                {okonomi.udestaaende_forfalden_antal != null && (
                  <div style={{ fontSize: 12, color: TEKST.sekundær, marginTop: 4 }}>
                    {okonomi.udestaaende_forfalden_antal} faktura{okonomi.udestaaende_forfalden_antal !== 1 ? 'er' : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Boks>
      </SammenklappeligSektion>

      {/* ── Lønforpligtelser ─────────────────────────────────────────────────── */}
      <Boks>
        <SektionsHeader
          label="Lønforpligtelser"
          højreIndhold={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {loenData?.total_md != null && (
                <span style={{ fontSize: 12, color: TEKST.sekundær }}>
                  Total: <span style={{ color: STATUS.konfigureret, fontWeight: 700 }}>{kroner(loenData.total_md)}/md.</span>
                </span>
              )}
            </div>
          }
          style={{ marginBottom: 12 }}
        />
        {loenFirstLoad ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
        ) : loenForpligtelser.length === 0 ? (
          <TomTilstand
            titel="Ingen lønforpligtelser registreret"
            besked="Opdateres automatisk fra Salary.dk — synkroniseres dagligt kl. 06:30."
            ikon="⬡"
            badge="IKKE FORBUNDET"
          />
        ) : (
          <>
            {/* Mobil: kort-liste — ingen horisontal scroll */}
            {isMobil ? (
              <LoenforpligtelserKortListe forpligtelser={loenForpligtelser} />
            ) : (
              <DataTabel
                kolonner={loenKolonner}
                rækker={loenForpligtelser}
                pageSize={0}
                tomTekst="Ingen lønforpligtelser"
              />
            )}
            <div style={{ marginTop: 10, fontSize: 11, color: TEKST.sekundær }}>
              Data: os.loenforpligtelser
              <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
            </div>
          </>
        )}
      </Boks>
    </div>
  );
}
